// Иконки UI v3 (README §15): тонкие линейные SVG инлайн, stroke 1.6–1.8,
// currentColor, viewBox 24. Пиктограммы типов элементов — viewBox 26×18,
// пути из метода pict(t) прототипа.

import type { CSSProperties } from 'react'

/** Пути 24×24. Одна строка d — один <path>. */
export const ICON_PATHS = {
  // оболочка
  pencil: ['M4 20h4L19 9l-4-4L4 16z', 'M13.5 6.5l4 4'],
  chevronRight: ['M9 6l6 6-6 6'],
  chevronLeft: ['M15 6l-6 6 6 6'],
  close: ['M6 6l12 12M18 6L6 18'],
  plus: ['M12 5v14M5 12h14'],
  search: ['M11 18a7 7 0 1 0 0-14a7 7 0 0 0 0 14zM20 20l-4-4'],
  // разделы (иконки полосы 56 — railIconD прототипа)
  chat: ['M4 5h16v11H9l-5 4z'],
  feed: ['M5 7h14M5 12h14M5 17h9'],
  flow: ['M6 4v6M6 10h6M12 10v4M12 14h6M6 10v10M6 20h6'],
  tracker: ['M4 4h4v16H4zM10 4h4v10h-4zM16 4h4v13h-4z'],
  folder: ['M4 5h5l2 2h9v12H4z'],
  library: ['M4 7h16v13H4zM8 7V4h8v3'],
  person: ['M12 12a4 4 0 1 0 0-8a4 4 0 0 0 0 8zM4 21c1.5-4 4.5-6 8-6s6.5 2 8 6'],
  book: ['M5 4h9a4 4 0 0 1 4 4v12H9a4 4 0 0 1-4-4z', 'M5 16a4 4 0 0 1 4-4h9'],
  draft: ['M5 3h10l4 4v14H5z', 'M9 12h6M9 16h4'],
  // Конструктор / трекер
  bulb: ['M12 3a6 6 0 0 0-3.5 10.9V17h7v-3.1A6 6 0 0 0 12 3z', 'M9.5 20.5h5'],
  board: ['M4 4h4v16H4zM10 4h4v10h-4zM16 4h4v13h-4z'],
  list: ['M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01'],
  archive: ['M3 5h18v4H3zM5 9v10h14V9M10 13h4'],
  fit: ['M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5'],
  plane: ['M21 4 3 11l6 2 2 6 3-4 5 4z', 'M9 13l12-9'],
  tag: ['M3 12V4h8l9 9-8 8z', 'M7.5 8h.01'],
  // ввод
  paperclip: ['M21 11.5 12.6 20a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4L15.5 7'],
  mic: ['M9 6a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z', 'M5 11a7 7 0 0 0 14 0M12 18v3'],
  copy: ['M8 8h12v12H8z', 'M16 8V4H4v12h4'],
  arrowDown: ['M12 5v14M6 13l6 6 6-6'],
} as const

export type IconName = keyof typeof ICON_PATHS

export interface IconProps {
  name: IconName
  /** px, по умолчанию 18 */
  size?: number
  /** толщина линии, 1.6–1.8 */
  stroke?: number
  className?: string
  style?: CSSProperties
  title?: string
}

export function Icon({ name, size = 18, stroke = 1.6, className, style, title }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
    >
      {title ? <title>{title}</title> : null}
      {ICON_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

// ---------- пиктограммы типов элементов (pict(t), §4.2) ----------

export type ElementType = 'engine' | 'orch' | 'sub' | 'skill' | 'mcp' | 'data'

/** [контур, залитый блок] — ровно как в прототипе. */
export const TYPE_PICT: Record<ElementType, [string, string]> = {
  engine: ['M3 2h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM6 5h14v8H6z', ''],
  orch: [
    'M9 9H4M17 9h5M13 6V3M4 9a1.5 1.5 0 1 1-3 0a1.5 1.5 0 1 1 3 0M25 9a1.5 1.5 0 1 1-3 0a1.5 1.5 0 1 1 3 0M14.5 2a1.5 1.5 0 1 1-3 0a1.5 1.5 0 1 1 3 0',
    'M10 6h6a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
  ],
  sub: [
    'M3 2h20a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z',
    'M12 8h8a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z',
  ],
  skill: ['M6 1.5h10l5 5v10H6zM16 1.5v5h5M9 10h9M9 13h6', ''],
  mcp: ['M6 3h14v12H6zM3 6v6M23 6v6M10 7h6M10 11h6', ''],
  data: ['M5 4.5a8 2.5 0 1 0 16 0a8 2.5 0 1 0-16 0M5 4.5v9a8 2.5 0 0 0 16 0v-9M5 9a8 2.5 0 0 0 16 0', ''],
}

/** Цвет типа (§4.2): движок --agent, оркестратор/субагент --ok, скил --soft,
 *  MCP --warn, хранилище --term-link. */
export const TYPE_COLOR: Record<ElementType, string> = {
  engine: 'var(--agent)',
  orch: 'var(--ok)',
  sub: 'var(--ok)',
  skill: 'var(--soft)',
  mcp: 'var(--warn)',
  data: 'var(--term-link)',
}

export const TYPE_LABEL: Record<ElementType, string> = {
  engine: 'движок',
  orch: 'оркестратор',
  sub: 'субагент',
  skill: 'скил',
  mcp: 'MCP',
  data: 'хранилище',
}

export interface TypeIconProps {
  type: ElementType
  /** ширина px; высота — по пропорции 26×18. По умолчанию 20 (строка над карточкой). */
  width?: number
  /** цвет; по умолчанию цвет типа */
  color?: string
  className?: string
  style?: CSSProperties
}

export function TypeIcon({ type, width = 20, color, className, style }: TypeIconProps) {
  const [outline, block] = TYPE_PICT[type]
  const c = color ?? TYPE_COLOR[type]
  return (
    <svg
      className={className}
      style={{ color: c, flexShrink: 0, ...style }}
      width={width}
      height={Math.round((width * 18) / 26)}
      viewBox="0 0 26 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={outline} />
      {block ? <path d={block} fill="currentColor" stroke="none" /> : null}
    </svg>
  )
}

// ---------- стрелка сворачивания ----------

export interface ChevronProps {
  /** раскрыто — стрелка вниз (поворот 90°) */
  open: boolean
  /** скрыть, но держать место (строки без детей) */
  hidden?: boolean
  className?: string
  style?: CSSProperties
}

/** Стрелка ▸/▾ меню (§1.2): 10×10 в ячейке 12×12, поворот .15s. */
export function Chevron({ open, hidden, className, style }: ChevronProps) {
  return (
    <span
      className={`abx-chev${open ? ' is-open' : ''}${className ? ' ' + className : ''}`}
      style={{ opacity: hidden ? 0 : 1, ...style }}
      aria-hidden="true"
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3.5 1.8 6.7 5 3.5 8.2" />
      </svg>
    </span>
  )
}
